// web/src/components/VoiceNoteRecorder.tsx
// The whole voice-note surface for MatchDetail:
//   - no note yet   → recorder (Stats/Reports) or upsell line (Free)
//   - pending       → "Transcribing…" + polls GET /match every 5s (max 3 min)
//   - done          → the transcript + delete
//   - failed        → message + recorder to try again
//
// Recording uses MediaRecorder (webm/opus on Chrome/Firefox/Edge, mp4/aac
// on Safari). 90-second cap. Upload is a presigned POST straight to S3
// (same pattern as photos), then /voice/confirm starts the Transcribe job.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMatchApi } from '../api/match';
import { Section } from './Container';
import { Divider } from './Divider';
import type { Match, Plan } from '../types';

const MAX_SEC = 90;
const POLL_MS = 5000;
const POLL_MAX = 36; // 3 min

interface Props {
  match: Match;
  kidId: string;
  matchId: string;
  plan: Plan | undefined;
  onMatch: (m: Match) => void;
}

function pickMime(): string {
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  if (typeof MediaRecorder === 'undefined') return '';
  return cands.find((c) => MediaRecorder.isTypeSupported(c)) ?? '';
}

function fmtClock(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function VoiceNoteSection({ match, kidId, matchId, plan, onMatch }: Props) {
  const api = useMatchApi();
  const status = match.voiceTranscriptStatus;

  // Poll while a transcription is in flight.
  useEffect(() => {
    if (status !== 'pending') return;
    let n = 0;
    const t = window.setInterval(async () => {
      n += 1;
      try {
        const fresh = await api.get(kidId, matchId);
        if (fresh.voiceTranscriptStatus !== 'pending') {
          onMatch(fresh);
          window.clearInterval(t);
        }
      } catch { /* transient — keep polling */ }
      if (n >= POLL_MAX) window.clearInterval(t);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [status, kidId, matchId, api, onMatch]);

  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.deleteVoice(kidId, matchId);
      onMatch({
        ...match,
        voiceNotePath: undefined,
        voiceTranscript: undefined,
        voiceTranscriptStatus: undefined,
        voiceTranscriptJob: undefined,
        voiceNoteDurationSec: undefined,
      });
    } finally {
      setDeleting(false);
    }
  };

  const paid = plan === 'stats' || plan === 'reports';

  return (
    <Section spacing="lg">
      <Divider label="Voice note" />

      {match.voiceTranscript ? (
        <>
          <div
            style={{
              padding: 'var(--space-base)',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 'var(--lede)',
              lineHeight: 1.6,
            }}
          >
            {match.voiceTranscript}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', fontSize: 'var(--small)', color: 'var(--muted)' }}>
            {match.voiceNoteDurationSec ? <span>{fmtClock(match.voiceNoteDurationSec)} recording · audio deleted after transcription</span> : null}
            <button type="button" onClick={remove} disabled={deleting} style={linkBtn}>
              {deleting ? 'Removing…' : 'Delete voice note'}
            </button>
          </div>
        </>
      ) : status === 'pending' ? (
        <p style={{ color: 'var(--muted)', margin: 0 }}>
          <span aria-hidden>🎙️</span> Transcribing your voice note — usually under a minute. This page will update itself.
        </p>
      ) : !paid ? (
        <p style={{ color: 'var(--muted)', margin: 0 }}>
          Say what happened and we'll write it down. Voice notes are part of{' '}
          <Link to="/upgrade" style={{ color: 'var(--green)' }}>Stats and Reports</Link>.
        </p>
      ) : (
        <>
          {status === 'failed' ? (
            <p style={{ color: 'var(--accent-coral)', margin: '0 0 10px', fontSize: 'var(--small)' }}>
              We couldn't transcribe that one. Try recording again.
            </p>
          ) : null}
          <Recorder
            onRecorded={async (blob, durationSec) => {
              const contentType = blob.type.split(';')[0]!.trim() || 'audio/webm';
              const presign = await api.presignVoice(kidId, matchId, contentType, blob.size, durationSec);
              const form = new FormData();
              Object.entries(presign.fields).forEach(([k, v]) => form.append(k, v));
              form.append('file', blob);
              const up = await fetch(presign.uploadUrl, { method: 'POST', body: form });
              if (!up.ok && up.status !== 204) throw new Error(`Upload failed (${up.status})`);
              await api.confirmVoice(kidId, matchId, presign.key, durationSec);
              onMatch({
                ...match,
                voiceNotePath: presign.key,
                voiceTranscriptStatus: 'pending',
                voiceNoteDurationSec: durationSec,
                voiceTranscript: undefined,
              });
            }}
          />
        </>
      )}
    </Section>
  );
}

// ─── Recorder ──────────────────────────────────────────────────────────────

function Recorder({ onRecorded }: { onRecorded: (blob: Blob, durationSec: number) => Promise<void> }) {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'uploading'>('idle');
  const [secs, setSecs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef<number | null>(null);

  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    pickMime() !== '';

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
  };

  useEffect(() => () => cleanupStream(), []);

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: pickMime() });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const durationSec = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        const blob = new Blob(chunksRef.current, { type: rec.mimeType });
        cleanupStream();
        setPhase('uploading');
        try {
          await onRecorded(blob, durationSec);
          setPhase('idle');
          setSecs(0);
        } catch (e) {
          setError((e as Error).message || 'Upload failed. Try again.');
          setPhase('idle');
        }
      };
      recRef.current = rec;
      startedAtRef.current = Date.now();
      rec.start(1000);
      setPhase('recording');
      setSecs(0);
      tickRef.current = window.setInterval(() => {
        const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSecs(s);
        if (s >= MAX_SEC) stop();
      }, 250);
    } catch (e) {
      const name = (e as DOMException)?.name;
      setError(
        name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow it in your browser settings and try again.'
          : 'Could not start recording.',
      );
      cleanupStream();
    }
  };

  const stop = () => {
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    recRef.current = null;
  };

  if (!supported) {
    return <p style={{ color: 'var(--muted)', margin: 0 }}>Recording isn't supported in this browser.</p>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {phase === 'idle' ? (
        <button type="button" onClick={start} style={primaryBtn}>
          <span aria-hidden>🎙️</span> Record a voice note
        </button>
      ) : phase === 'recording' ? (
        <>
          <button type="button" onClick={stop} style={{ ...primaryBtn, background: 'var(--accent-coral)' }}>
            <span aria-hidden>■</span> Stop
          </button>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-sans)', fontWeight: 600 }}>
            <span aria-hidden style={{ color: 'var(--accent-coral)', marginRight: 6 }}>●</span>
            {fmtClock(secs)} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>/ {fmtClock(MAX_SEC)}</span>
          </span>
        </>
      ) : (
        <span style={{ color: 'var(--muted)' }}>Uploading…</span>
      )}
      {phase === 'idle' ? (
        <span style={{ color: 'var(--muted)', fontSize: 'var(--small)' }}>
          Up to 90 seconds. We transcribe it and delete the audio.
        </span>
      ) : null}
      {error ? <span style={{ color: 'var(--accent-coral)', fontSize: 'var(--small)', flexBasis: '100%' }}>{error}</span> : null}
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 18px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--green)',
  color: 'var(--ink-on-green)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--small)',
  cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--muted)',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: 'var(--small)',
  fontFamily: 'inherit',
};
